
import { NavLink, useParams } from "react-router-dom";
import { Careers } from "../components/careerList";
import SubNav from "../components/subNav";
import SEO from '../components/SEO';
import styles from "../styles/careers.module.css";
import { FaBookmark, FaRegBookmark, FaFilter, FaTimes, FaSearch } from "react-icons/fa";
import { useState, useEffect } from "react";
import { baseUrl } from '../config';

// Search Bar component styled similar to the ebook search
const CareerSearchBar = ({ searchTerm, setSearchTerm }) => (
    <div className={styles.careerSearchContainer}>
        <div className={styles.searchInputWrapper}>
            <FaSearch className={styles.searchIcon} />
            <input 
                type="text" 
                placeholder="Search jobs by title, role, location, or description..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.careerSearchInput}
            />
            {searchTerm && (
                <button 
                    className={styles.clearSearchButton}
                    onClick={() => setSearchTerm("")}
                >
                    <FaTimes />
                </button>
            )}
        </div>
    </div>
);

const CombinedFilterBubbles = ({
    locations, selectedLocations, toggleLocation,
    jobTypes, selectedJobTypes, toggleJobType,
    roles, selectedRoles, toggleRole,
    genres, selectedGenres, toggleGenre
  }) => (
    <div className={styles.filterBubblesSection}>
      <div className={styles.filterBubblesContainer}>
        {/* Location bubbles */}
        {locations.map(location => (
          <button
            key={`location-${location}`}
            className={`${styles.filterBubble} ${selectedLocations.includes(location) ? styles.filterBubbleActive : ''}`}
            onClick={() => toggleLocation(location)}
          >
            📍 {location}
          </button>
        ))}
        
        {/* Job Type bubbles */}
        {jobTypes.map(jobType => (
          <button
            key={`jobType-${jobType}`}
            className={`${styles.filterBubble} ${selectedJobTypes.includes(jobType) ? styles.filterBubbleActive : ''}`}
            onClick={() => toggleJobType(jobType)}
          >
            ⏱️ {jobType}
          </button>
        ))}
        
        {/* Role bubbles */}
        {roles.map(role => (
          <button
            key={`role-${role}`}
            className={`${styles.filterBubble} ${selectedRoles.includes(role) ? styles.filterBubbleActive : ''}`}
            onClick={() => toggleRole(role)}
          >
            👤 {role}
          </button>
        ))}
        
        {/* Genre bubbles */}
        {genres.map(genre => (
          <button
            key={`genre-${genre}`}
            className={`${styles.filterBubble} ${selectedGenres.includes(genre) ? styles.filterBubbleActive : ''}`}
            onClick={() => toggleGenre(genre)}
          >
            🎬 {genre}
          </button>
        ))}
      </div>
    </div>
  );

export function AllAvailCareers() {
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        bookmarked: false,
        locations: [],
        jobTypes: [],
        salaryMin: "",
        salaryMax: "",
        roles: [],
        genres: []
    });
    
    // Get all unique locations, job types, and roles from Careers data
    const uniqueLocations = [...new Set(Careers.map(career => career.location))];
    const uniqueJobTypes = [...new Set(Careers.map(career => career.jobType))];
    const uniqueRoles = [...new Set(Careers.map(career => career.role))];
    const uniqueGenres = [...new Set(Careers.map(career => career.genre))];
    
    // Get favorites from localStorage
    const getFavorites = () => {
        const saved = localStorage.getItem('favoriteJobs');
        return saved ? JSON.parse(saved) : [];
    };

    // Generic toggle function for any filter type (location, jobType, role, genre)
    const toggleFilterItem = (filterType, item) => {
        setFilters(prevFilters => {
            const currentItems = [...prevFilters[filterType]];
            const itemIndex = currentItems.indexOf(item);
            
            if (itemIndex === -1) {
                // Item not selected, add it
                return {
                    ...prevFilters,
                    [filterType]: [...currentItems, item]
                };
            } else {
                // Item already selected, remove it
                currentItems.splice(itemIndex, 1);
                return {
                    ...prevFilters,
                    [filterType]: currentItems
                };
            }
        });
    };
    
    // Filter logic to be passed to the CareersList
    const filterJobs = (jobs) => {
        const favorites = getFavorites();
        
        return jobs.filter(job => {
            // Filter by search term
            if (searchTerm && 
                !job.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
                !job.role.toLowerCase().includes(searchTerm.toLowerCase()) &&
                !job.location.toLowerCase().includes(searchTerm.toLowerCase()) &&
                !job.desc.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }
            
            if (filters.bookmarked && !favorites.includes(job.id)) {
                return false;
            }
            
            if (filters.locations.length > 0 && !filters.locations.includes(job.location)) {
                return false;
            }
            
            if (filters.jobTypes.length > 0 && !filters.jobTypes.includes(job.jobType)) {
                return false;
            }
            
            if (filters.roles.length > 0 && !filters.roles.includes(job.role)) {
                return false;
            }

            if (filters.genres.length > 0 && !filters.genres.includes(job.genre)) {
                return false;
            }
            
            if (filters.salaryMin || filters.salaryMax) {
                const salaryRangeMatch = job.salary ? job.salary.match(/\$([0-9,]+)-\$([0-9,]+)/) : null;
                if (salaryRangeMatch) {
                    const minSalary = parseInt(salaryRangeMatch[1].replace(/,/g, ''));
                    const maxSalary = parseInt(salaryRangeMatch[2].replace(/,/g, ''));
                    
                    if (filters.salaryMin && minSalary < parseInt(filters.salaryMin)) {
                        return false;
                    }
                    
                    if (filters.salaryMax && maxSalary > parseInt(filters.salaryMax)) {
                        return false;
                    }
                } else if (filters.salaryMin || filters.salaryMax) {
                    return false;
                }
            }
            
            return true;
        });
    };
    
    // Reset all filters
    const resetFilters = () => {
        setFilters({
            bookmarked: false,
            locations: [],
            jobTypes: [],
            salaryMin: "",
            salaryMax: "",
            roles: [],
            genres: []
        });
        setSearchTerm("");
    };
    
    // Calculate active filters count for the filter button (now only for bookmarked and salary)
    const activeFiltersCount = (
        (filters.bookmarked ? 1 : 0) +
        (filters.salaryMin ? 1 : 0) +
        (filters.salaryMax ? 1 : 0)
    );
    
    return (
        <main id={styles.allCareers}>
            <SEO
              title="Film Industry Careers"
              description="Explore job opportunities in the film and entertainment industry. Find roles for directors, producers, crew, cast, and more on ProducerBuddy."
              url="https://producerbuddy.pilomation.com/careers"
            />
            <SubNav />
            <div className={styles.listWrapper}>
                <h1><span className={styles.movieTitle}>All</span> Jobs</h1>
                
                {/* Enhanced search bar component */}
                <CareerSearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                
                <CombinedFilterBubbles 
                locations={uniqueLocations}
                selectedLocations={filters.locations}
                toggleLocation={(location) => toggleFilterItem('locations', location)}
                
                jobTypes={uniqueJobTypes}
                selectedJobTypes={filters.jobTypes}
                toggleJobType={(jobType) => toggleFilterItem('jobTypes', jobType)}
                
                roles={uniqueRoles}
                selectedRoles={filters.roles}
                toggleRole={(role) => toggleFilterItem('roles', role)}
                
                genres={uniqueGenres}
                selectedGenres={filters.genres}
                toggleGenre={(genre) => toggleFilterItem('genres', genre)}
                />
                
                {/* Filter button now only for bookmarked and salary filters */}
                <div className={styles.filterButtonContainer}>
                    <button 
                        className={styles.filterButton}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <FaFilter /> Advanced Filters
                        {activeFiltersCount > 0 && 
                            <span className={styles.filterBadge}>{activeFiltersCount}</span>
                        }
                    </button>
                </div>
                
                {/* Filter panel - now only contains bookmarked and salary filters */}
                {showFilters && (
                    <div className={styles.filterPanel}>
                        <div className={styles.filterHeader}>
                            <h3>Advanced Filters</h3>
                            <button 
                                className={styles.resetButton}
                                onClick={resetFilters}
                            >
                                Reset All
                            </button>
                            <button 
                                className={styles.closeFilterButton}
                                onClick={() => setShowFilters(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        
                        {/* Bookmarked filter */}
                        <div className={styles.filterSection}>
                            <h4>Saved Jobs</h4>
                            <label className={styles.checkboxContainer}>
                                <input 
                                    type="checkbox" 
                                    checked={filters.bookmarked}
                                    onChange={() => setFilters({...filters, bookmarked: !filters.bookmarked})}
                                />
                                <span className={styles.checkmark}></span>
                                Show only bookmarked jobs
                            </label>
                        </div>
                        
                        {/* Salary Range filter */}
                        <div className={styles.filterSection}>
                            <h4>Salary Range</h4>
                            <div className={styles.salaryInputs}>
                                <div>
                                    <label>Min ($):</label>
                                    <input 
                                        type="number" 
                                        placeholder="e.g. 40000"
                                        value={filters.salaryMin}
                                        onChange={(e) => setFilters({...filters, salaryMin: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label>Max ($):</label>
                                    <input 
                                        type="number" 
                                        placeholder="e.g. 80000"
                                        value={filters.salaryMax}
                                        onChange={(e) => setFilters({...filters, salaryMax: e.target.value})}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Filtered jobs list */}
                <FilteredCareersList filterFunction={filterJobs} />
            </div>
        </main>
    );
}

function FilteredCareersList({ filterFunction }) {
    const [filteredCareers, setFilteredCareers] = useState(Careers);
    
    useEffect(() => {
        setFilteredCareers(filterFunction(Careers));
    }, [filterFunction]);
    
    // Favorites state for bookmarking
    const [favorites, setFavorites] = useState(() => {
        const saved = localStorage.getItem('favoriteJobs');
        return saved ? JSON.parse(saved) : [];
    });

    const toggleFavorite = (jobId) => {
        const updatedFavorites = favorites.includes(jobId)
            ? favorites.filter(id => id !== jobId)
            : [...favorites, jobId];
        
        setFavorites(updatedFavorites);
        localStorage.setItem('favoriteJobs', JSON.stringify(updatedFavorites));
    };
    
    // Import CareerItem from careerList.js or define it inline
    const CareerItem = ({ career, isFavorite, onToggleFavorite }) => {
        const handleFavoriteClick = (e) => {
            e.preventDefault();
            onToggleFavorite(career.id);
        };

        return (
            <NavLink to={"/careers/" + career.id}>
                <div key={career.id} className={styles.careerWrapper}>
                    <div 
                        id={styles.saveJob} 
                        onClick={handleFavoriteClick}
                    >
                        {isFavorite ? <FaBookmark /> : <FaRegBookmark />}
                    </div>
                    <h3>
                        <span className={styles.movieTitle}>{career.title}</span>
                    </h3>
                    
                    <div className={styles.listingJobVitals}>
                        <div className={styles.vitalItem}>
                            <span className={styles.vitalIcon}>👤</span>
                            <span className={styles.vitalValue}>{career.role || "Not specified"}</span>
                        </div>
                        <div className={styles.vitalItem}>
                            <span className={styles.vitalIcon}>📍</span>
                            <span className={styles.vitalValue}>{career.location || "Location not specified"}</span>
                        </div>

                        <div className={styles.vitalItem}>
                            <span className={styles.vitalIcon}>🎬</span>
                            <span className={styles.vitalValue}>{career.genre || "Genre not specified"}</span>
                        </div>
                        
                        <div className={styles.vitalItem}>
                            <span className={styles.vitalIcon}>⏱️</span>
                            <span className={styles.vitalValue}>{career.jobType || "Job type not specified"}</span>
                        </div>
                        
                        <div className={styles.vitalItem}>
                            <span className={styles.vitalIcon}>💰</span>
                            <span className={styles.vitalValue}>{career.salary || "Salary not specified"}</span>
                        </div>
                    </div>
                    
                    <p>{career.desc}</p>
                    
                    <div className={styles.deadlineInfo}>
                        <span className={styles.postedDate}>Posted: {career.postedDate || "Date not specified"}</span>
                        <span className={styles.applicationDeadline}>Apply by: {career.applicationDeadline || "Open until filled"}</span>
                    </div>
                </div>  
            </NavLink>  
        );
    };

    // Display filtered careers or a message if none found
    return (
        <div className={styles.careerListWrapper}>
            {filteredCareers.length > 0 ? (
                filteredCareers.map(career => (
                    <CareerItem 
                        key={career.id} 
                        career={career} 
                        isFavorite={favorites.includes(career.id)}
                        onToggleFavorite={toggleFavorite}
                    />
                ))
            ) : (
                <div className={styles.noResultsMessage}>
                    <p>No jobs match your current filters.</p>
                    <p>Try adjusting your filters or search criteria.</p>
                </div>
            )}
        </div>
    );
}

export function Career(){
    const {id} = useParams();
    
    // Add state for favorites
    const [favorites, setFavorites] = useState(() => {
        const saved = localStorage.getItem('favoriteJobs');
        return saved ? JSON.parse(saved) : [];
    });

    // Function to toggle favorite status
    const toggleFavorite = () => {
        const updatedFavorites = favorites.includes(Number(id))
            ? favorites.filter(jobId => jobId !== Number(id))
            : [...favorites, Number(id)];
        
        setFavorites(updatedFavorites);
        localStorage.setItem('favoriteJobs', JSON.stringify(updatedFavorites));
    };
    
    const CareersItems = Careers.map(career =>
    {if (id == career.id) {
        return(
        <div key={career.id} id={styles.oneCareerWrapper}>
            {/* Add save icon */}
            <div 
                id={styles.saveJob} 
                onClick={toggleFavorite}
                style={{
                    position: 'absolute', 
                    top: '10px', 
                    right: '10px', 
                    cursor: 'pointer'
                }}
            >
                {favorites.includes(Number(id)) ? <FaBookmark /> : <FaRegBookmark />}
            </div>
            
            {/* Centered title */}
            <h1 style={{ textAlign: 'center' }}>
                <span className={styles.movieTitle}>{career.title}</span>
                <br/>
            </h1>
            
            {/* Highlighted vital information section */}
            <div className={styles.jobVitalsContainer}>
                <div className={styles.jobVitals}>
                    <div className={styles.vitalItem}>
                        <span className={styles.vitalIcon}>👤</span>
                        <span className={styles.vitalLabel}>Role:</span>
                        <span className={styles.vitalValue}>{career.role || "Not specified"}</span>
                    </div>
                    
                    <div className={styles.vitalItem}>
                        <span className={styles.vitalIcon}>📍</span>
                        <span className={styles.vitalLabel}>Location:</span>
                        <span className={styles.vitalValue}>{career.location || "Not specified"}</span>
                    </div>
                    
                    <div className={styles.vitalItem}>
                        <span className={styles.vitalIcon}>⏱️</span>
                        <span className={styles.vitalLabel}>Job Type:</span>
                        <span className={styles.vitalValue}>{career.jobType || "Not specified"}</span>
                    </div>
                    
                    <div className={styles.vitalItem}>
                        <span className={styles.vitalIcon}>💰</span>
                        <span className={styles.vitalLabel}>Salary:</span>
                        <span className={styles.vitalValue}>{career.salary || "Not specified"}</span>
                    </div>
                    
                    <div className={styles.vitalItem}>
                        <span className={styles.vitalIcon}>🗓️</span>
                        <span className={styles.vitalLabel}>Posted:</span>
                        <span className={styles.vitalValue}>{career.postedDate || "Not specified"}</span>
                    </div>
                    
                    <div className={styles.vitalItem}>
                        <span className={styles.vitalIcon}>⏳</span>
                        <span className={styles.vitalLabel}>Deadline:</span>
                        <span className={styles.vitalValue}>{career.applicationDeadline || "Open until filled"}</span>
                    </div>

                    <div className={styles.vitalItem}>
                        <span className={styles.vitalIcon}>🎬</span>
                        <span className={styles.vitalLabel}>Genre:</span>
                        <span className={styles.vitalValue}>{career.genre || "Not specified"}</span>
                    </div>
                </div>
            </div>
            
            {/* Full description with heading */}
            <h4 className={styles.descriptionHeading}>Job Description</h4>
            <p>{career.fullDesc}</p>
        </div> 
        )}})
    return(
        <>
            <SubNav />
            <div className={styles.careerListWrapper}>
                <NavLink to='/careers' id={styles.backLink}><button id={styles.careerBackButton}>Back</button></NavLink>
                {CareersItems}
                <NavLink to={"/careers/apply/" + id} id={styles.apply}><button>Apply!</button></NavLink>
            </div>
        </>    
    )}

export function Apply(){
    const {id} = useParams();

    const handleSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
    
        try {
            const response = await fetch(`${baseUrl}/apply`, {
                method: 'POST',
                body: formData,
            });
            const data = await response.json();
            //alert(data.message); // Show success message
        } catch (error) {
        }
    };

    const application = Careers.map(career =>
        {if(id == career.id){
            return(
                <div id={styles.info}>
                    <h1>Application for <span className={styles.movieTitle}>{career.title}</span>
                    <br/>
                    <span className={styles.roleHeader}>{career.role}</span></h1>
                    <p>{career.desc}</p>
                </div>
            )
        }}
    )
    return(
        <main>
            <SubNav />
            <div id={styles.applicationWrapper}>
                <NavLink to='/careers' id={styles.applyBackLink}><button id={styles.applyBackButton}>Back</button></NavLink>
                {application}
                <form action="" method="post" onSubmit={handleSubmit}>
                    <div>
                    <label htmlFor="firstName">First Name</label>
                        <input type="text" name="first_name" id={styles.firstName} required placeholder="First Name"  />
                        
                        <label htmlFor="lastName">Last Name</label>
                        <input type="text" name="last_name" id={styles.lastName} required placeholder="Last Name"  />
                        
                        <label htmlFor="mobileNumber">Mobile Number</label>
                        <input type="text" name="mobile_number" id={styles.mobileNumber} required placeholder="Mobile Number"/>
                        
                        <label htmlFor="email">Email</label>
                        <input type="text" name="email" id={styles.email} placeholder="Email" required />
                        
                        <label htmlFor="address1">Address Line 1</label>
                        <input type="text" name="address1" id={styles.address1} placeholder="Address Line 1"  />
                        
                        <label htmlFor="address2">Address Line 2</label>
                        <input type="text" name="address2" id={styles.address2} placeholder="Address Line 2" />
                        
                        <label htmlFor="postcode">Postcode</label>
                        <input type="text" name="postcode" id={styles.postcode} placeholder="Postcode"  />
                        
                        <label htmlFor="state">State</label>
                        <input type="text" name="state" id={styles.state} placeholder="State"  />
                        
                        <label htmlFor="country">Country</label>
                        <input type="text" name="country" id={styles.country} placeholder="Country" />
                        
                        <label htmlFor="resume">Upload Resume</label>
                        <input type="file" name="resume" id={styles.resume}  />
                        
                        <label htmlFor="audition" id={styles.auditionLabel}>Upload Audition</label>
                        <input type="file" name="audition" id={styles.audition} required/>
                        
                        <label htmlFor="height">Height</label>
                        <input type="text" name="height" id={styles.height} placeholder="Height" />
                        
                        <label htmlFor="gender">Gender</label>
                        <input type="text" name="gender" id={styles.gender} placeholder="Gender"  />
                        
                        <label htmlFor="age">Age</label>
                        <input type="number" name="age" id={styles.age} placeholder="Age"  />
                        
                        <label htmlFor="other">Other information</label>
                        <textarea name="other_info" id={styles.other} cols="2" rows="2" placeholder="Other Information, like hair color."></textarea>
                        
                        <label htmlFor="otherDocs">Other Documents</label>
                        <input type="file" name="other_docs" id={styles.otherDocs} />
                    </div>
                    <button>Submit Application</button>
                </form>
            </div>
        </main>
    )
}
